import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import threadApi from "../../api/threadApi";
import ReplyItem from "./ReplyItem";

/**
 * ReplyList Component
 *
 * Displays nested replies under messages with pagination support.
 * Handles reply loading, expand/collapse functionality, and reply counts.
 * Requirements: 3.4, 7.1
 *
 * @param {Object} props - Component props
 * @param {string} props.messageId - The message ID to load replies for
 * @param {number} props.initialReplyCount - Initial reply count from message
 * @param {Object} props.currentUser - Current user data for permissions
 * @param {Function} props.onReplyInteraction - Callback for reply interactions
 * @param {boolean} props.autoExpand - Whether to auto-expand replies
 * @param {boolean} props.hideToggleButton - Whether to hide the toggle button
 * @param {string} props.highlightReplyId - Reply ID to highlight (from notifications)
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Ref for exposing addReply method
 */
const ReplyList = forwardRef(
  (
    {
      messageId,
      initialReplyCount = 0,
      currentUser,
      onReplyInteraction,
      autoExpand = false,
      hideToggleButton = false,
      highlightReplyId,
      className = "",
    },
    ref
  ) => {
    // State management
    const [state, setState] = useState({
      replies: [],
      isExpanded: autoExpand || !!highlightReplyId, // Auto-expand if highlighting a reply
      isLoading: false,
      isLoadingMore: false,
      error: null,
      pagination: {
        page: 1,
        hasMore: false,
        total: initialReplyCount,
      },
    });

    /**
     * Update state helper
     */
    const updateState = useCallback((updates) => {
      setState((prev) => ({ ...prev, ...updates }));
    }, []);

    /**
     * Check if replies should be shown
     */
    const shouldShowReplies = useMemo(() => {
      return state.isExpanded && (state.replies.length > 0 || state.isLoading);
    }, [state.isExpanded, state.replies.length, state.isLoading]);

    /**
     * Load replies from API
     */
    const loadReplies = useCallback(
      async (page = 1, append = false) => {
        if (!messageId) {
          return;
        }

        updateState({
          isLoading: page === 1 && !append,
          isLoadingMore: page > 1 || append,
          error: null,
        });

        try {
          const response = await threadApi.getReplies(messageId, page, 20);

          const newReplies = append
            ? [...state.replies, ...response.replies]
            : response.replies;

          updateState({
            replies: newReplies,
            isLoading: false,
            isLoadingMore: false,
            pagination: {
              page: response.pagination.page,
              hasMore: response.pagination.hasMore,
              total: response.pagination.total,
            },
          });
        } catch (error) {
          console.error("Failed to load replies:", error);

          updateState({
            isLoading: false,
            isLoadingMore: false,
            error: error.message || "Failed to load replies",
          });
        }
      },
      [messageId, state.replies, updateState]
    );

    /**
     * Auto-expand when highlighting a reply
     */
    useEffect(() => {
      if (highlightReplyId && !state.isExpanded) {
        console.log("🔍 ReplyList: Auto-expanding for highlighted reply:", {
          messageId,
          highlightReplyId,
          currentlyExpanded: state.isExpanded,
        });
        updateState({ isExpanded: true });
        // Load replies if not already loaded
        if (state.replies.length === 0 && !state.isLoading) {
          console.log(
            "📥 ReplyList: Loading replies to find highlighted reply"
          );
          loadReplies(1);
        }
      }
    }, [
      highlightReplyId,
      state.isExpanded,
      state.replies.length,
      state.isLoading,
      loadReplies,
      updateState,
      messageId,
    ]);

    /**
     * Handle expand/collapse toggle
     */
    const handleToggle = useCallback(async () => {
      const newExpanded = !state.isExpanded;

      updateState({ isExpanded: newExpanded });

      // Load replies when expanding for the first time
      if (newExpanded && state.replies.length === 0 && !state.isLoading) {
        await loadReplies(1, false);
      }
    }, [
      state.isExpanded,
      state.replies.length,
      state.isLoading,
      loadReplies,
      updateState,
    ]);

    /**
     * Handle load more replies
     */
    const handleLoadMore = useCallback(async () => {
      if (state.isLoadingMore || !state.pagination.hasMore) {
        return;
      }

      await loadReplies(state.pagination.page + 1, true);
    }, [
      state.isLoadingMore,
      state.pagination.hasMore,
      state.pagination.page,
      loadReplies,
    ]);

    /**
     * Handle reply interactions (like, menu actions, etc.)
     */
    const handleReplyInteraction = useCallback(
      (replyId, action, data) => {
        // Handle local state updates for optimistic updates
        if (action === "like" && data.optimistic) {
          updateState({
            replies: state.replies.map((reply) =>
              reply._id === replyId
                ? {
                    ...reply,
                    hasUserLiked: data.isLiked,
                    isLiked: data.isLiked, // Keep both for compatibility
                    likeCount: data.likeCount,
                    likes: data.isLiked
                      ? [
                          ...(reply.likes || []),
                          currentUser?._id || currentUser?.auth0Id,
                        ].filter(Boolean)
                      : (reply.likes || []).filter(
                          (id) =>
                            id !== currentUser?._id &&
                            id !== currentUser?.auth0Id
                        ),
                  }
                : reply
            ),
          });
        }

        // Handle rollback for failed optimistic updates
        if (action === "like" && data.rollback) {
          updateState({
            replies: state.replies.map((reply) =>
              reply._id === replyId
                ? {
                    ...reply,
                    hasUserLiked: data.isLiked,
                    isLiked: data.isLiked, // Keep both for compatibility
                    likeCount: data.likeCount,
                  }
                : reply
            ),
          });
        }

        // Forward to parent handler
        if (onReplyInteraction) {
          onReplyInteraction(replyId, action, data);
        }
      },
      [state.replies, currentUser, onReplyInteraction, updateState]
    );

    /**
     * Add new reply to the list (called from parent when reply is posted)
     */
    const addReply = useCallback(
      (newReply) => {
        updateState({
          replies: [...state.replies, newReply],
          pagination: {
            ...state.pagination,
            total: state.pagination.total + 1,
          },
        });

        // Expand replies if not already expanded
        if (!state.isExpanded) {
          updateState({ isExpanded: true });
        }
      },
      [state.replies, state.pagination, state.isExpanded, updateState]
    );

    /**
     * Expose addReply method and handleToggle to parent via ref
     */
    useImperativeHandle(
      ref,
      () => ({
        addReply,
        handleToggle,
      }),
      [addReply, handleToggle]
    );

    /**
     * Auto-expand if autoExpand is true and there are replies
     */
    useEffect(() => {
      if (
        autoExpand &&
        initialReplyCount > 0 &&
        !state.isExpanded &&
        state.replies.length === 0
      ) {
        handleToggle();
      }
    }, [
      autoExpand,
      initialReplyCount,
      state.isExpanded,
      state.replies.length,
      handleToggle,
    ]);

    // Don't render anything if there are no replies and no expand button needed
    if (
      initialReplyCount === 0 &&
      state.pagination.total === 0 &&
      state.replies.length === 0
    ) {
      return null;
    }

    return (
      <div className={`reply-list ${className}`}>
        {/* Expand/Collapse Button - Instagram style */}
        {!hideToggleButton &&
          (initialReplyCount > 0 || state.pagination.total > 0) && (
            <div className="reply-toggle-container mb-3">
              <button
                onClick={handleToggle}
                disabled={state.isLoading}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50 font-normal"
              >
                {state.isLoading ? (
                  <span className="flex items-center space-x-1">
                    <svg
                      className="w-3 h-3 animate-spin"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    <span>Loading...</span>
                  </span>
                ) : (
                  <span>
                    {state.isExpanded
                      ? `Hide replies`
                      : `View ${
                          state.pagination.total || initialReplyCount
                        } more ${
                          (state.pagination.total || initialReplyCount) === 1
                            ? "reply"
                            : "replies"
                        }`}
                  </span>
                )}
              </button>
            </div>
          )}

        {/* Replies Container */}
        {shouldShowReplies && (
          <div className="replies-container ml-12 space-y-3">
            {/* Loading State */}
            {state.isLoading && state.replies.length === 0 && (
              <div className="reply-loading py-4">
                <div className="flex items-center space-x-2 text-gray-500">
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <span className="text-sm">Loading replies...</span>
                </div>
              </div>
            )}

            {/* Reply Items */}
            {state.replies.map((reply, index) => {
              const isHighlighted = highlightReplyId === reply._id;

              return (
                <ReplyItem
                  key={reply._id}
                  reply={reply}
                  currentUser={currentUser}
                  onInteraction={handleReplyInteraction}
                  isFirst={index === 0}
                  isLast={index === state.replies.length - 1}
                  isHighlighted={isHighlighted}
                  className=""
                />
              );
            })}

            {/* Load More Button */}
            {state.pagination.hasMore && (
              <div className="load-more-container py-2">
                <button
                  onClick={handleLoadMore}
                  disabled={state.isLoadingMore}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                >
                  {state.isLoadingMore ? (
                    <span className="flex items-center space-x-1">
                      <svg
                        className="w-3 h-3 animate-spin"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      <span>Loading...</span>
                    </span>
                  ) : (
                    "Load more replies"
                  )}
                </button>
              </div>
            )}

            {/* Error State */}
            {state.error && (
              <div className="reply-error py-3">
                <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span>{state.error}</span>
                    <button
                      onClick={() => loadReplies(1, false)}
                      className="ml-2 text-red-700 hover:text-red-900 underline"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!state.isLoading && state.replies.length === 0 && !state.error && (
              <div className="reply-empty py-4">
                <p className="text-sm text-gray-500 italic">
                  No replies yet. Be the first to reply!
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

ReplyList.displayName = "ReplyList";

export default ReplyList;
